import { Modal, Button, Form, Col, Row } from 'react-bootstrap';
import { Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import Select from 'react-select';
import moment from 'moment';
import { generateTimeSlots } from '../../helpers';

const timeOptions = (times) =>
  times.map((time) => ({ label: time, value: time }));

const EditOvertimeSlot = ({
  slotDetail,
  showEdit,
  handleShowEdit,
  handleUpdateSlot,
}) => {
  const validationSchema = Yup.object().shape({
    slotCount: Yup.number()
      .typeError('Must be a number')
      .required('Slot count is required')
      .min(1, 'At least 1 slot required'),

    otStart: Yup.string().required('Start time is required'),

    otEnd: Yup.string()
      .required('End time is required')
      .test(
        'end-after-start',
        'End time must be after start time',
        function (value) {
          // eslint-disable-next-line react/no-this-in-sfc
          const { otStart } = this.parent;
          if (!otStart || !value) return true;
          const start = moment(otStart, 'HH:mm');
          const end = moment(value, 'HH:mm');
          return end.isAfter(start);
        }
      ),
  });

  return (
    <>
      <Button
        variant="white"
        className="btn btn-sm btn-white"
        onClick={() => handleShowEdit(true, slotDetail._id)}
      >
        <i className="mdi mdi-pencil" /> Edit
      </Button>

      <Modal show={showEdit} onHide={() => handleShowEdit(false)} centered>
        <Modal.Header>
          <Modal.Title>Edit Overtime Slot</Modal.Title>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => handleShowEdit(false)}
          />
        </Modal.Header>

        <Formik
          enableReinitialize
          initialValues={{
            slotId: slotDetail?._id || '',
            slotCount: slotDetail?.slotCount || '',
            otStart: slotDetail?.otStart || '',
            otEnd: slotDetail?.otEnd || '',
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            handleUpdateSlot(values);
          }}
        >
          {(formikprops) => (
            <Form onSubmit={formikprops.handleSubmit}>
              <Modal.Body>
                <Row>
                  <Col md={6}>
                    {/* Start Time */}
                    <Form.Group className="mb-3">
                      <Form.Label>Slot Start Time</Form.Label>
                      <Select
                        name="otStart"
                        options={timeOptions(generateTimeSlots('00:00'))}
                        value={
                          formikprops.values.otStart
                            ? {
                                label: formikprops.values.otStart,
                                value: formikprops.values.otStart,
                              }
                            : null
                        }
                        onChange={(option) => {
                          formikprops.setFieldValue(
                            'otStart',
                            option.value,
                            true
                          );
                          formikprops.setFieldTouched('otEnd', true, false); // mark as touched
                        }}
                        theme={(theme) => ({
                          ...theme,
                          colors: {
                            ...theme.colors,
                            primary: '#0649b5',
                            neutral10: '#edf2f9',
                            neutral20: '#d2ddec',
                            neutral30: '#d2ddec',
                            neutral50: '#b1c2da',
                            neutral80: '#12263f',
                            dangerLight: '#DEEBFF',
                            danger: '#12263f',
                          },
                        })}
                        styles={{
                          option: (provided, state) => ({
                            ...provided,
                            color: state.data.isButton ? '#0649b5' : 'inherit',
                            fontWeight: state.data.isButton ? 'bold' : 'normal',
                            fontSize: state.data.isButton ? '12px' : 'inherit',
                          }),
                        }}
                      />

                      <ErrorMessage
                        name="otStart"
                        render={(msg) => (
                          <small className="text-danger">{msg}</small>
                        )}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    {/* End Time */}
                    <Form.Group className="mb-3">
                      <Form.Label>Slot End Time</Form.Label>
                      <Select
                        name="otEnd"
                        options={timeOptions(generateTimeSlots('00:15'))}
                        value={
                          formikprops.values.otEnd
                            ? {
                                label: formikprops.values.otEnd,
                                value: formikprops.values.otEnd,
                              }
                            : null
                        }
                        onChange={(option) => {
                          formikprops.setFieldValue(
                            'otEnd',
                            option.value,
                            true
                          ); // value + validate
                          formikprops.setFieldTouched('otEnd', true, false); // mark as touched
                        }}
                        theme={(theme) => ({
                          ...theme,
                          colors: {
                            ...theme.colors,
                            primary: '#0649b5',
                            neutral10: '#edf2f9',
                            neutral20: '#d2ddec',
                            neutral30: '#d2ddec',
                            neutral50: '#b1c2da',
                            neutral80: '#12263f',
                            dangerLight: '#DEEBFF',
                            danger: '#12263f',
                          },
                        })}
                        styles={{
                          option: (provided, state) => ({
                            ...provided,
                            color: state.data.isButton ? '#0649b5' : 'inherit',
                            fontWeight: state.data.isButton ? 'bold' : 'normal',
                            fontSize: state.data.isButton ? '12px' : 'inherit',
                          }),
                        }}
                      />

                      <ErrorMessage
                        name="otEnd"
                        render={(msg) => (
                          <small className="text-danger">{msg}</small>
                        )}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                {/* Slot Count */}
                <Form.Group className="mb-3">
                  <Form.Label>Slot Count</Form.Label>
                  <Field
                    name="slotCount"
                    type="number"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="slotCount"
                    component="small"
                    className="text-danger"
                  />
                </Form.Group>
                <br />
                <h5 className="mb-0">
                  <b>Note:</b> Start and end times are displayed in 24-hour
                  format, similar to Excel
                </h5>
              </Modal.Body>

              <Modal.Footer className="d-flex justify-content-end">
                <Button variant="primary" type="submit">
                  Save
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
};

EditOvertimeSlot.propTypes = {
  slotDetail: PropTypes.shape({
    _id: PropTypes.string,
    slotCount: PropTypes.number,
    otStart: PropTypes.string,
    otEnd: PropTypes.string,
  }),
  showEdit: PropTypes.bool.isRequired,
  handleShowEdit: PropTypes.func.isRequired,
  handleUpdateSlot: PropTypes.func.isRequired,
};

export default EditOvertimeSlot;
